import type { HTMLAttributes } from 'react';

import { cn } from '../cn';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import styles from './quillStyles.module.scss';

export type MessageBodyProps = HTMLAttributes<HTMLDivElement> & {
  /** HTML content to render */
  html: string;
  /** Show only the first line of content */
  firstLineOnly?: boolean;
  /** Enable error highlighting for contact-specific variables (e.g., when viewing a specific contact) */
  showContactErrors?: boolean;
};

/**
 * Renders HTML message content with proper styling for mentions, variables, and documents.
 * Used for displaying email/message bodies from campaigns.
 */
export function MessageBody({
  html,
  className,
  firstLineOnly,
  showContactErrors,
  ...props
}: MessageBodyProps) {
  // Generate Quill/TipTap mention styling classes
  const quillClasses = cn(
    styles.wrapper,
    styles.showMyVariablesErrors,
    styles.showUndefinedDocuments,
    styles.showDocumentUrls,
    showContactErrors && styles.showContactVariablesErrors,
    showContactErrors && styles.showCurlyBracketsErrors,
  );

  // Base Tailwind classes
  const baseClasses = cn(
    'text-sm leading-relaxed wrap-break-word overflow-hidden whitespace-pre-line',
    !firstLineOnly && 'line-clamp-10',
    firstLineOnly && 'line-clamp-1 whitespace-nowrap text-ellipsis',
  );

  // Combine all classes
  const classList = cn(baseClasses, className, quillClasses);

  // Process HTML
  let displayHtml = html;
  if (firstLineOnly) {
    // Get first line by splitting on <p>, <div>, or <br> tags
    const firstLine = html.split(/<\/?(?:p|div|br)[^>]*>/).find((line) => line.trim().length > 0) || '';
    displayHtml = `<p>${firstLine}</p>`;
  } else {
    // Normalize HTML to handle different formats
    displayHtml = displayHtml.replace(/<br\s*\/?>\s*(?!<\/p>|<\/div>)/gi, '</p><p>');

    // Handle content wrapped in a single paragraph with <br> tags
    if (displayHtml.startsWith('<p>') && displayHtml.endsWith('</p>')) {
      const innerContent = displayHtml.substring(3, displayHtml.length - 4);
      if (innerContent.includes('<br')) {
        const paragraphs = innerContent.split(/<br\s*\/?>/gi).map((p) => `<p>${p}</p>`);
        displayHtml = paragraphs.join('');
      }
    }

    // Handle content without HTML tags
    if (!displayHtml.includes('<p>') && !displayHtml.includes('<div>')) {
      displayHtml = displayHtml
        .split('\n')
        .map((line) => (line.trim() ? `<p>${line}</p>` : '<p>&nbsp;</p>'))
        .join('');
    }

    // Ensure empty paragraphs have non-breaking space
    displayHtml = displayHtml.replace(/<p>\s*<\/p>/gi, '<p>&nbsp;</p>');
    displayHtml = displayHtml.replace(/<\/p><p><\/p><p>/gi, '</p><p>&nbsp;</p><p>');
  }

  // Add Tailwind classes to HTML elements
  const processedHtml = displayHtml
    .replace(/<p>/g, '<p class="text-sm leading-6 text-gray-800 dark:text-white py-2 first:pt-0 last:pb-0 min-h-[1.5em] block m-0">')
    .replace(/<span/g, '<span class="text-sm leading-6 text-gray-800 dark:text-white m-0"')
    .replace(/<div/g, '<div class="my-2 first:mt-0 last:mb-0"')
    .replace(/<pre/g, '<pre class="whitespace-pre-wrap font-inherit m-0"')
    .replace(/<img/g, '<img class="max-w-full"')
    .replace(/<br\s*\/?>/g, '<br class="block mt-3" />');

  return (
    <div
      className={classList}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(processedHtml) }}
      {...props}
    />
  );
}
